import React from "react";
import { Calculation, Selection } from "../../../../../src/application/Contracts";
import { round } from "lodash";
import { TwsPercentLine } from "../custom-fields/tws-percent-line/TwsPercentLine";
import { RenderStepInputReadonlyField } from "../custom-fields/Helper";
import formModelStore from "../../../../store/FormModelStore";
import TWSPumpType = Calculation.TWSPumpType;
import InputMethod = Calculation.InputMethod;
import WaterSystemOptionModel = Calculation.WaterSystemOptionModel;
import SelectedWaterSystemPrimaryPressureErrors = Calculation.SelectedWaterSystemPrimaryPressureErrors;
import { IsInstantaneousWasSelectedOnPreviousStep, IsProductSelected } from "../../../utils/FormModelStoreTWSHelper";


export interface WaterSystemTechnicalDataComponent extends Calculation.WaterSystemTechnicalDataModel {
    grid?: number;
    tankWeight?: number;
    optionsWeight?: number;
}
export class WaterSystemTechnicalData extends React.Component<WaterSystemTechnicalDataComponent> {
    renderErrorRow = (message: string) => {
        if (!message) {
            return <></>
        }
        return <tr className={`error-row`}>
            <td className={`error-cell`} colSpan={2}>{__(message)}</td>
        </tr>
    }
    renderRow = (label: string, value: any, unit?: string, errorValue?: boolean, untranslatableLabel?: string) => {
        if (!value) {
            return <></>
        }
        let cellError = errorValue ? `error-cell` : ``;
        return <tr>
            <td>{__(label)} {untranslatableLabel}</td>
            <td className={cellError}>{value} {unit}</td>
        </tr>
    }

    getPrimaryPump = (value: any): string => {
        switch (value) {
            case TWSPumpType.No:
                return __("No");
            case TWSPumpType.Single:
                return __("Single");
            default:
                return __("Double");
        }
    }

    getPressureLabelForPrimaryPump = () => {
        return formModelStore.formModel.waterSystem?.waterSystemSelection?.primaryPump?.value != 0 ? "Free pressure" : "Pressure drop";
    }

    getPrimaryPressureLabel = () => {
        return formModelStore.formModel.waterSystem?.selectedTapWaterSystemModel?.value?.cv == "3P" ? "Min primary free pressure" : "Max primary pressure drop";
    }

    getGridAmount = () => {
        return `step-inputs-row grid-${this.props.grid ?? 2}`;
    }

    renderPrimaryPumpSection = () => {
        return <div className="card properties-card">
            <div className={'card-header'}>
                {__("Primary")}
            </div>
            <div className="card-body">
                <table className={`step-stripped-table`}>
                    <tbody className="left-text-align">
                        {this.renderRow("Max primary service pressure", this.props.primaryPumpModel.maxPressure, "barg")}
                        {this.renderRow("Max primary service temperature", this.props.primaryPumpModel.maxTemperature, "°C")}
                        {this.renderRow("Primary inlet temperature", this.props.primaryPumpModel.primaryInletTemperature, "°C")}
                        {this.renderRow("Primary outlet temperature", this.props.primaryPumpModel.primaryOutletTemperature, "°C")}
                        {this.renderRow("Max primary flow rate", this.props.primaryPumpModel.primaryFlowRate, "m3/h")}
                        {this.renderRow(this.getPrimaryPressureLabel(), this.props.primaryPumpModel.freePressure ?? this.props.primaryPumpModel.primaryFreePressure, "kPa", this.props.primaryPumpModel.freePressureError != SelectedWaterSystemPrimaryPressureErrors.NoError)}
                        {this.renderErrorRow(this.getPrimaryPumpError())}
                        {this.renderRow("Primary pump", this.getPrimaryPump(this.props.primaryPumpModel.primaryPump))}
                        {this.renderRow("Pump type", this.props.primaryPumpModel.pumpType)}
                        {this.renderRow("Control valve", this.getValveName())}
                        {this.renderRow("Control valve type", this.props.primaryPumpModel.controlValveType)}
                        {this.renderRow("Control valve actuator", this.props.primaryPumpModel.ctrlValveActuator)}
                        {this.renderRow("Primary inlet connection diameter", this.props.primaryPumpModel.inletConnectionDiameter)}
                        {this.renderRow("Primary outlet connection diameter", this.props.primaryPumpModel.outletConnectionDiameter)}
                        {this.renderRow("Extra prim kit art.No", this.props.primaryPumpModel.extraPrimKit)}
                        {this.renderAlfaPilotData()}
                    </tbody>
                </table>
            </div>
        </div>
    }


    getPrimaryPumpError = () => {
        if (this.props.primaryPumpModel.freePressureError == SelectedWaterSystemPrimaryPressureErrors.SelectBiggerValve) {
            return "Select a bigger valve DN";
        }
        if (this.props.primaryPumpModel.freePressureError == SelectedWaterSystemPrimaryPressureErrors.ConsultWithCtt) {
            return "Please consult Cetetherm";
        }
        return "";
    }

    getPressureLabelForSecondaryPump = () => {
        return IsInstantaneousWasSelectedOnPreviousStep() || IsProductSelected(Selection.ProductFamily.AquaPlate) ? "Max pressure drop" : "Min free pressure";
    }
    renderSecondaryPumpSection = () => {
        return <div className="card properties-card">
            <div className={'card-header'}>
                {__("Secondary")}
            </div>
            <div className="card-body">
                <table className={`step-stripped-table`}>
                    <tbody className="left-text-align">
                        {this.renderRow("Max operating pressure", this.props.secondaryPumpModel.maxPressure, "barg")}
                        {this.renderRow("Max secondary service temperature", this.props.secondaryPumpModel.maxTemperature, "°C")}
                        {this.renderRow("Secondary inlet temperature", this.props.secondaryPumpModel.secondaryInletTemperature, "°C")}
                        {this.renderRow("Secondary outlet temperature", this.props.secondaryPumpModel.secondaryOutletTemperature, "°C")}
                        {this.renderRow(this.getPressureLabelForSecondaryPump(), this.props.secondaryPumpModel.pressureDropAtRequestedCapacity && round(this.props.secondaryPumpModel.pressureDropAtRequestedCapacity), "kPa")}
                        {this.renderRow("Secondary pump", this.getPrimaryPump(this.props.secondaryPumpModel.secondaryPump))}
                        {this.renderRow("Pump type", this.props.secondaryPumpModel.pumpType)}
                        {this.renderRow("Secondary flow rate", this.props.secondaryPumpModel.secondaryFlowRate && round(this.props.secondaryPumpModel.secondaryFlowRate, 2), "m3/h")}
                        {formModelStore.formModel.inputMethod.inputMethod.value != InputMethod.ProductCatalog && this.renderRow("10 mins flow rate", this.props.secondaryPumpModel.tenMinFlowRate, "l/10 mins")}
                        {formModelStore.formModel.inputMethod.inputMethod.value != InputMethod.ProductCatalog && this.renderRow("One hour flow rate", this.props.secondaryPumpModel.oneHourFlowRate, "l/h")}
                        {this.renderRow("Max recycling flow rate", this.props.secondaryPumpModel.maxRecyclingFlowRate, "m3/h")}
                        {this.renderRow("Tank charging time*", this.props.secondaryPumpModel.tankChargingTime, "mins")}
                        {this.renderRow("Secondary inlet connection diameter", this.props.secondaryPumpModel.secInletConnectionDiameter)}
                        {this.renderRow("Secondary outlet connection diameter", this.props.secondaryPumpModel.secOutletConnectionDiameter)}
                        {this.renderRow("Setting valve", this.props.secondaryPumpModel.settingValve)}
                    </tbody>
                </table>
            </div>
        </div>
    }


    renderAlfaPilotData = () => {
        if (!this.props.primaryPumpModel.alfaPilotOptionSelected) {
            return <></>
        }
        return <React.Fragment>
            {this.renderRow("", __("AlfaPilot option selected"))}
            {this.renderRow("Selected option", this.props.primaryPumpModel.selectedAquaEfficiencyOption)}
            {this.renderRow("Extra CV Kv", this.props.primaryPumpModel.extraCvKv)}
            {this.renderRow(`Pressure drop at`, this.props.primaryPumpModel.pressureDropAtPrimaryFlowRate, 'kPa', undefined, ` ${this.props.primaryPumpModel.primaryFlowRate} m3/h`)}
        </React.Fragment>
    }

    field = () => <TwsPercentLine value={this.props.marginToMaxCapacityPercent} maxValue={40} />
    getHeaterExchangerTypeName = () => {
        if (!this.props.heatExchangerModel.heatExchangerType) {
            return null;
        }
        if (this.props.heatExchangerModel.heatExchangerType === "P") {
            return __("Plates & Gaskets");
        }
        if (this.props.heatExchangerModel.heatExchangerType === "B") {
            return __("Copper Brazed");
        }
        return __("Fusion Bonded")
    }
    getValveName = () => {
        if (this.props.primaryPumpModel.controlValve === "3P") {
            return __("3 Port");
        }
        if (this.props.primaryPumpModel.controlValve === "2PE") {
            return __("2 Port");
        }
        if (this.props.primaryPumpModel.controlValve === "3Pdiv") {
            return __("3P diverting");
        }
        return __("No")
    }
    renderHeatExchanger = () => {
        return <div className="card properties-card">
            <div className={'card-header'}>
                {__("Heat Exchanger")}
            </div>
            <div className="card-body">
                <table className={`step-stripped-table`}>
                    <tbody className="left-text-align">
                        {this.renderRow("Heat exchanger type", this.getHeaterExchangerTypeName())}
                        {this.renderRow("Plates type", this.props.heatExchangerModel.platesType)}
                        {this.renderRow("Plates number", this.props.heatExchangerModel.platesNumber)}
                        {this.renderRow("Insulation", this.props.heatExchangerModel.insulation)}
                    </tbody>
                </table>
            </div>
        </div>
    }
    renderGeneral = () => {
        return <div className="card properties-card">
            <div className={'card-header'}>
                {__("General")}
            </div>
            <div className="card-body">
                <table className={`step-stripped-table`}>
                    <tbody className="left-text-align">
                        {this.renderRow("Controller", this.props.generalModel.controller)}
                        {this.renderRow("Max elec consumption", this.props.generalModel.maxElectConsumption)}
                        {this.renderRow("Overall dimensions", this.props.generalModel.overallDimensions, "mm")}
                        {this.renderRow("Total weight", this.props.generalModel.twsWeight + (this.props.optionsWeight ?? 0) + (this.props.tankWeight ?? 0), "kg")}
                    </tbody>
                </table>
            </div>
        </div>
    }

    render() {
        return <React.Fragment>
            <div className={this.getGridAmount()}>
                {RenderStepInputReadonlyField("Product family", this.props.productFamilyDisplayName)}
                {RenderStepInputReadonlyField("Product series", this.props.productSeries)}
                {RenderStepInputReadonlyField("Product article number", this.props.productArticleNumber)}
                {RenderStepInputReadonlyField("Temperature program", this.props.temperatureProgram)}
                {formModelStore.formModel.inputMethod.inputMethod.value != InputMethod.ProductCatalog && RenderStepInputReadonlyField("Required capacity", this.props.requiredCapacity && round(this.props.requiredCapacity), "kW")}
                {RenderStepInputReadonlyField("Max capacity", this.props.nominalCapacity, "kW")}
                {formModelStore.formModel.inputMethod.inputMethod.value != InputMethod.ProductCatalog && RenderStepInputReadonlyField("Margin to max capacity (%)", this.field())}
            </div>
            <div className="step-inputs-row grid-2">
                {this.renderPrimaryPumpSection()}
                {this.renderSecondaryPumpSection()}
            </div>
            <div className="step-inputs-row grid-2">
                {this.renderHeatExchanger()}
                {this.renderGeneral()}
            </div>
        </React.Fragment>
    }
}